import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";
import ClientProjectView from "./ClientProjectView";

export default function ClientOverview() {
  const { profile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.client_id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  async function load() {
    setLoading(true);
    const [projectsRes, invoicesRes] = await Promise.all([
      supabase.from("projects").select("id, name").eq("client_id", profile.client_id).order("created_at"),
      supabase
        .from("invoices")
        .select("*")
        .eq("client_id", profile.client_id)
        .order("invoice_date", { ascending: false })
        .limit(3),
    ]);
    setProjects(projectsRes.data || []);
    setInvoices(invoicesRes.data || []);
    setLoading(false);
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  if (projects.length === 0) {
    return (
      <div>
        <h1 className="text-xl font-semibold text-foreground mb-1">Overview</h1>
        <p className="text-sm text-muted-foreground">
          No projects set up yet. Your agency contact will get this started.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-5">Overview</h1>

      <ClientProjectView projectId={projects[0].id} />

      {projects.length > 1 && (
        <div className="border border-border rounded-2xl bg-card overflow-hidden mt-5">
          <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">
            Other projects
          </div>
          {projects.slice(1).map((p) => (
            <Link
              key={p.id}
              to={`/dashboard/client/projects/${p.id}`}
              className="flex items-center justify-between px-5 py-3 border-b border-border last:border-0 hover:bg-accent/60"
            >
              <span className="text-sm font-medium">{p.name}</span>
              <span className="text-xs text-muted-foreground">View</span>
            </Link>
          ))}
        </div>
      )}

      <div className="border border-border rounded-2xl bg-card overflow-hidden mt-5">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <span className="font-semibold text-sm text-foreground">Recent invoices</span>
          <Link to="/dashboard/client/invoices" className="text-xs text-muted-foreground">
            View all
          </Link>
        </div>
        {invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground p-5">No invoices yet.</p>
        ) : (
          invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-0 text-sm">
              <span>{inv.invoice_number}</span>
              <span className="text-muted-foreground">₹{Number(inv.amount).toLocaleString("en-IN")}</span>
              <span className="font-mono text-[10.5px] bg-accent text-foreground rounded-full px-2.5 py-1">
                {inv.status}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
